/**
 * SlashCommandMenu
 *
 * Detects when user types "/" in the chatbot input and shows a menu of
 * AI generation slash commands. Selecting a command inserts it into the
 * input as a prefix. When sent, the message is routed to the AI generation
 * pipeline instead of regular chat.
 *
 * Supports commands: /generate-test, /generate-body, /generate-pre-script,
 * /diagnose, /generate (full request), /generate-collection.
 *
 * Requirements: 24.1, 24.2, 24.3, 24.4
 */

import { useMemo } from 'react';
import { Sparkles, TestTube2, FileJson, Settings, Bug, Layers, Wand2 } from 'lucide-react';

/**
 * AI generation slash commands with their metadata.
 * Each command maps to a generation mode sent to the backend.
 */
export const AI_GENERATION_COMMANDS = [
  {
    command: '/generate-test',
    label: 'Generate Test Script',
    description: 'Generate post-response test assertions based on current response',
    icon: TestTube2,
    mode: 'test_script',
    targetTab: 'Post-Script',
  },
  {
    command: '/generate-body',
    label: 'Generate Request Body',
    description: 'Generate a realistic JSON request body from description',
    icon: FileJson,
    mode: 'request_body',
    targetTab: 'Body',
  },
  {
    command: '/generate-pre-script',
    label: 'Generate Pre-Script',
    description: 'Generate pre-request script for setup, auth, or variables',
    icon: Settings,
    mode: 'pre_script',
    targetTab: 'Pre-Script',
  },
  {
    command: '/generate',
    label: 'Generate Full Request',
    description: 'Describe an API request in plain English — AI fills everything',
    icon: Wand2,
    mode: 'full_request',
    targetTab: null,
  },
  {
    command: '/diagnose',
    label: 'Diagnose Error',
    description: 'Analyze the last error response and suggest a fix',
    icon: Bug,
    mode: 'diagnose_error',
    targetTab: null,
  },
  {
    command: '/generate-collection',
    label: 'Generate Collection',
    description: 'Generate a full CRUD set of requests for a resource',
    icon: Layers,
    mode: 'generate_collection',
    targetTab: null,
  },
];

/**
 * Check if the given input text starts with a generation slash command.
 *
 * @param {string} text - The input text to check
 * @returns {{ isGenerationCommand: boolean, command: Object|null, prompt: string }}
 */
export function parseGenerationCommand(text) {
  if (!text || !text.startsWith('/')) {
    return { isGenerationCommand: false, command: null, prompt: '' };
  }

  const trimmed = text.trim();

  for (const cmd of AI_GENERATION_COMMANDS) {
    if (trimmed === cmd.command || trimmed.startsWith(cmd.command + ' ')) {
      const prompt = trimmed.slice(cmd.command.length).trim();
      return { isGenerationCommand: true, command: cmd, prompt };
    }
  }

  return { isGenerationCommand: false, command: null, prompt: '' };
}

/**
 * SlashCommandMenu component
 *
 * Renders a dropdown menu of AI generation slash commands, filtered by
 * what the user has typed after "/". Selecting a command inserts it into
 * the chat input.
 *
 * @param {Object} props
 * @param {string} props.filter - Text after '/' for filtering commands
 * @param {Function} props.onSelect - Called with the selected command object
 * @param {Function} props.onClose - Called to close the menu
 * @param {boolean} [props.visible=true] - Whether to show the menu
 */
// eslint-disable-next-line no-unused-vars
export function SlashCommandMenu({ filter = '', onSelect, onClose, visible = true }) {
  const filtered = useMemo(() => {
    if (!filter) return AI_GENERATION_COMMANDS;
    const lower = filter.toLowerCase();
    return AI_GENERATION_COMMANDS.filter((cmd) => {
      return (
        cmd.command.toLowerCase().includes(lower) ||
        cmd.label.toLowerCase().includes(lower) ||
        cmd.description.toLowerCase().includes(lower)
      );
    });
  }, [filter]);

  if (!visible || filtered.length === 0) return null;

  return (
    <div className="mb-2 max-h-64 overflow-y-auto bg-background border border-border rounded-lg shadow-lg">
      <div className="px-3 py-1.5 text-label text-foreground-secondary font-medium uppercase tracking-wider border-b border-border flex items-center gap-1.5">
        <Sparkles className="w-3 h-3 text-purple-400" />
        AI Generation Commands
      </div>
      {filtered.map((cmd) => {
        const Icon = cmd.icon;
        return (
          <button
            key={cmd.command}
            onClick={() => onSelect(cmd)}
            className="w-full text-left px-3 py-2 hover:bg-card flex items-center gap-2.5 transition-colors"
          >
            <Icon className="w-4 h-4 text-purple-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground truncate">
                  {cmd.command}
                </span>
                <span className="text-label text-foreground-secondary truncate">
                  {cmd.label}
                </span>
              </div>
              <div className="text-xs text-foreground-secondary truncate mt-0.5">
                {cmd.description}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

export default SlashCommandMenu;
